import { Typography } from '@tunasong/ui-lib'
import { createTunaEditor, useEditorClass, useFromEntityLinks } from '@tunasong/editor'
import styles from './print.module.css'
import { graphHooks } from '@tunasong/graph-lib/react'
import { elementSort, matchAny } from '@tunasong/models'
import { useYDoc } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isFolderContainer, isPage, isSong } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useMemo } from 'react'

export interface PrintViewProps {
  entity: Persisted<Entity>
}

export const PrintView: FC<PrintViewProps> = props => {
  const { entity } = props
  const PerformanceEditor = useEditorClass('PerformanceEditor')

  const entityFilter = matchAny(isSong, isPage)

  /** For folders, we include only songs and not pages */
  const { entities: folderEntities } = graphHooks.useEntityChildren({ parentId: entity.id, filter: isSong })

  /** @todo we should have a single Editor.extractLinks() method that does not require an editor with plugins */
  const { value } = useYDoc(entity)
  const rootEditor = useMemo(() => {
    if (!value) {
      return null
    }
    const rootEditor = createTunaEditor(entity.id, [], { graph: {} as never }, 'labs')
    rootEditor.children = value
    return rootEditor
  }, [entity, value])

  /** Extract the links, get the linked entities by ID */
  const linkedEntities = useFromEntityLinks(rootEditor)

  const entities = useMemo(
    () =>
      isFolderContainer(entity)
        ? elementSort(folderEntities, entity.options?.sort)
        : [entity, ...linkedEntities.filter(entityFilter)],

    [entity, folderEntities, linkedEntities, entityFilter]
  )

  return (
    <>
      {entities.map(entity => (
        <React.Fragment key={entity.name}>
          <Typography variant="h1" className={styles.pageTitle}>
            {entity.name}
          </Typography>
          <PerformanceEditor key={entity.id} element={entity} readOnly={true} />
        </React.Fragment>
      ))}
    </>
  )
}
